"use client";

import React, { memo } from "react";
import {
  Box,
  Breadcrumb,
  HStack,
  Menu,
  Portal,
  Text,
  Tokens,
} from "@chakra-ui/react";
import GenericIcon, { type GenericIconType } from "../actions/GenericIcon";
import type { GenericOptionItem } from "../controls/option.types";

type MenuSelectDetails = {
  value: string;
};

export type BreadcrumbOption<T extends string = string> = GenericOptionItem<T>;

export type WithBreadcrumbItemProps<T extends string = string> = {
  activeState: T;
  setActiveState: (value: T) => void;
  options: BreadcrumbOption<T>[];
  withMenu?: boolean;
  disabled?: boolean;
  warningMessage?: string;
  isCurrentPage?: boolean;
  separatorPosition?: "start" | "end";
  zIndex?: Tokens["zIndex"];
  minWidth?: string;
  indicatorIcon?: GenericIconType;
  indicatorIconColor?: string;
};

function getActiveBreadcrumbOption<T extends string>(
  options: BreadcrumbOption<T>[],
  activeState: T,
) {
  return options.find((option) => option.value === activeState);
}

function renderBreadcrumbTriggerLabel<T extends string>(
  activeOption: BreadcrumbOption<T> | undefined,
  warningMessage?: string,
) {
  return (
    <HStack as="span" gap="2">
      <Text as="span">{activeOption?.label ?? "Select..."}</Text>
      {warningMessage ? (
        <Text
          as="span"
          fontSize="xs"
          color="moveon.warning"
          fontWeight="normal"
        >
          {warningMessage}
        </Text>
      ) : null}
    </HStack>
  );
}

function WithBreadcrumbItemInner<T extends string = string>({
  activeState,
  setActiveState,
  options,
  withMenu = true,
  disabled = false,
  warningMessage,
  isCurrentPage = false,
  separatorPosition,
  zIndex = "max",
  minWidth,
  indicatorIcon = "slash_forward",
  indicatorIconColor = "moveon.surface.fg",
}: WithBreadcrumbItemProps<T>) {
  const activeOption = getActiveBreadcrumbOption(options, activeState);
  const resolvedIndicatorIconColor = disabled
    ? "moveon.text.muted"
    : indicatorIconColor;

  if (isCurrentPage || !withMenu) {
    return (
      <Breadcrumb.Item>
        <Breadcrumb.CurrentLink>
          {activeOption?.label || "Unknown"}
        </Breadcrumb.CurrentLink>
      </Breadcrumb.Item>
    );
  }

  // Render with menu dropdown
  return (
    <>
      {separatorPosition === "start" && (
        <Breadcrumb.Separator>/</Breadcrumb.Separator>
      )}
      <Breadcrumb.Item>
        <Menu.Root
          onSelect={(details: MenuSelectDetails) =>
            setActiveState(details.value as T)
          }
        >
          <Menu.Trigger asChild disabled={disabled}>
            <Breadcrumb.Link
              as="button"
              type="button"
              aria-disabled={disabled}
              color={disabled ? "moveon.text.muted" : undefined}
              cursor={disabled ? "not-allowed" : "pointer"}
              opacity={disabled ? 0.55 : undefined}
              _disabled={{
                color: "moveon.text.muted",
                cursor: "not-allowed",
                opacity: 0.55,
              }}
            >
              {renderBreadcrumbTriggerLabel(activeOption, warningMessage)}
              <Box as="span" color={resolvedIndicatorIconColor}>
                <GenericIcon icon={indicatorIcon} />
              </Box>
            </Breadcrumb.Link>
          </Menu.Trigger>
          <Portal>
            <Menu.Positioner>
              <Menu.Content
                bg="moveon.surface.bg"
                color="moveon.surface.fg"
                borderColor="moveon.surface.border"
                zIndex={zIndex}
                minW={minWidth}
              >
                {options.map((option) => (
                  <Menu.Item
                    key={String(option.value)}
                    value={option.value}
                    bg={
                      option.value === activeState
                        ? "moveon.secondaryLight"
                        : undefined
                    }
                    fontWeight={
                      option.value === activeState ? "bold" : undefined
                    }
                  >
                    {option.label}
                  </Menu.Item>
                ))}
              </Menu.Content>
            </Menu.Positioner>
          </Portal>
        </Menu.Root>
      </Breadcrumb.Item>
      {separatorPosition === "end" && (
        <Breadcrumb.Separator>/</Breadcrumb.Separator>
      )}
    </>
  );
}

// Prevent re-renders when props are identical
const WithBreadcrumbItem = memo(WithBreadcrumbItemInner) as <
  T extends string = string,
>(
  props: WithBreadcrumbItemProps<T>,
) => React.ReactElement;

export default WithBreadcrumbItem;
